// CSV import dialog: choose a file, match columns to fields, see errors before anything is written, check the
// whole file on the server, import in batches, and look at the journal of the past imports.

import { useCallback, useMemo, useState } from 'react';
import { canWrite, uuidv7, type Id } from '@confdeck/core-shared';
import { useAccess, useMeta, useSync } from '../sync/react.tsx';
import { FIELD_ICONS, Icon } from '../ui/icons.tsx';
import { IconButton, Button, Dialog, Input, Menu, MenuContent, MenuItem, MenuTrigger } from '../ui/primitives.tsx';
import { guessMapping, parseCsv, prepare, type Prepared } from './csv.ts';
import { fetchImportRuns, type ImportRun } from './runs.ts';
import { importableFields, templateCsv, templateName } from './template.ts';
import s from './import.module.css';

const BATCH = 2000;
/** A row of the file and the line it came from. */
type Miss = { line: number; reason: string };

export function ImportDialog({ typeId, open, onClose }: { typeId: Id; open: boolean; onClose: () => void }) {
  return <Dialog open={open} onOpenChange={(o) => !o && onClose()} label="Импорт CSV">{open && <Body typeId={typeId} onClose={onClose} />}</Dialog>;
}

function Body({ typeId, onClose }: { typeId: Id; onClose: () => void }) {
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  const type = meta.type(typeId);
  const fields = meta.fields(typeId);
  // A file may carry only the fields a person may write: not the app's own, not hidden.
  const loadable = importableFields(fields, [...access.hidden]);
  const [rows, setRows] = useState<string[][] | null>(null);
  const [mapping, setMapping] = useState<(string | null)[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A stage of the rows or the setting «Причина при правке» may ask for a reason (R-C14, R-C15): asked once the server says so (F audit №2).
  const [reason, setReason] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: Miss[] } | null>(null);
  const [checked, setChecked] = useState<{ imported: number; skipped: Miss[] } | null>(null);
  const [runs, setRuns] = useState<ImportRun[] | null>(null);
  const headers = rows?.[0] ?? [];
  const body = rows?.slice(1) ?? [];
  // A linked record is created only in a section the person may write.
  const canCreate = useCallback((target: Id) => canWrite(access, target), [access]);
  const prepared: Prepared | null = useMemo(
    () => (rows ? prepare(rows.slice(1), mapping, typeId, client.store, uuidv7, canCreate) : null),
    [rows, mapping, typeId, client.store, canCreate],
  );

  const pickFile = async (f: File) => {
    const parsed = parseCsv(await f.text());
    if (parsed.length < 2) { setError('В файле нет строк с данными'); return; }
    setRows(parsed);
    setMapping(guessMapping(parsed[0]!, loadable));
    setFileName(f.name);
    setError(null);
    setResult(null);
    setChecked(null);
  };

  /**
   * Runs the file on the server: a check writes nothing and shows what the load would do, a load writes the rows
   * in batches and leaves a trace in the journal.
   */
  const run = async (dryRun: boolean) => {
    if (!prepared) return;
    setProgress({ done: 0, total: prepared.rows.length });
    setError(null);
    if (dryRun) setChecked(null); else setResult(null);
    try {
      if (!dryRun) {
        for (const t of prepared.newTerms) {
          const r = await fetch('/api/commands/term.create', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id: t.id, taxonomyId: t.taxonomyId, parentId: null, name: t.name }) });
          if (!r.ok) throw new Error(((await r.json()) as { error: { message: string } }).error.message);
        }
        // Linked records the file names but nobody has yet: they are created before the rows that point at them.
        for (const rec of prepared.newRecords) {
          const titleKey = meta.type(rec.typeId)?.titleField ?? 'name';
          const r = await fetch('/api/commands/record.create', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id: rec.id, typeId: rec.typeId, data: { [titleKey]: rec.title } }) });
          if (!r.ok) throw new Error(`связанная запись «${rec.title}»: ${((await r.json()) as { error: { message: string } }).error.message}`);
        }
      }
      let imported = 0;
      const skipped: Miss[] = [];
      for (let i = 0; i < prepared.rows.length; i += BATCH) {
        const batch = prepared.rows.slice(i, i + BATCH);
        const r = await fetch('/api/commands/record.import', {
          method: 'POST', headers: { 'content-type': 'application/json', ...(reason?.trim() ? { 'x-change-reason': encodeURIComponent(reason.trim()) } : {}) },
          body: JSON.stringify({
            typeId,
            rows: batch.map(({ id, data }) => ({ id, data })),
            dryRun,
            fileName,
            // The linked records the load is about to make: a check does not make them, but the file is whole.
            planned: prepared.newRecords.map((rec) => ({ id: rec.id, typeId: rec.typeId, title: rec.title })),
          }),
        });
        if (!r.ok) {
          const err = ((await r.json()) as { error: { message: string; fields?: Record<string, string> } }).error;
          if (err.fields?.$reason && reason === null) setReason('');
          throw new Error(err.fields?.$reason ? `${err.message} — напишите причину ниже и повторите` : err.message);
        }
        // The command answer is wrapped: { syncId, result }.
        const res = ((await r.json()) as { result: { imported: number; skipped: { row: number; reason: string }[] } }).result;
        imported += res.imported;
        // The server counts rows inside the batch; the file line of the first row of the batch is the offset.
        for (const miss of res.skipped) skipped.push({ line: batch[0]!.line + miss.row - 1, reason: miss.reason });
        setProgress({ done: Math.min(prepared.rows.length, i + BATCH), total: prepared.rows.length });
      }
      if (dryRun) setChecked({ imported, skipped });
      else { setResult({ imported, skipped }); setRuns(null); }
      setProgress(null);
    } catch (e) {
      setError(dryRun
        ? `Проверка остановлена: ${(e as Error).message}.`
        : `Импорт остановлен: ${(e as Error).message}. Уже загруженные пачки сохранены.`);
      setProgress(null);
    }
  };

  /** The template of the section: an empty file with the header of the fields a person may load. */
  const downloadTemplate = () => {
    const name = templateName(type?.name ?? 'раздел');
    const url = URL.createObjectURL(new Blob([templateCsv(loadable)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** The journal of the section: the past imports with their counters and reasons. */
  const showRuns = async () => {
    if (runs) { setRuns(null); return; }
    try { setRuns(await fetchImportRuns(typeId)); }
    catch (e) { setError(`Журнал загрузок недоступен: ${(e as Error).message}`); }
  };

  return (
    <div className={s.box}>
      <header className={s.head}><Icon name="table" size={16} /><span className={s.headTitle}>Импорт в «{type?.nameMany}»</span><IconButton size="sm" icon="close" label="Закрыть" onClick={onClose} /></header>
      {!rows ? (
        <label className={s.drop}>
          <input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && void pickFile(e.target.files[0])} />
          <Icon name="plus" size={18} />
          <span>Выберите CSV-файл</span>
          <span className={s.sub}>Первая строка — названия колонок. Подойдёт выгрузка из Excel, amoCRM или Битрикс24.</span>
          <span className={s.sub}>Нет файла? <button type="button" className={s.link} onClick={(e) => { e.preventDefault(); downloadTemplate(); }}>Скачать шаблон</button> — пустой файл с названиями полей.</span>
        </label>
      ) : (
        <>
          <div className={s.map}>
            {headers.map((h, i) => (
              <div key={i} className={s.mapRow}>
                <span className={s.col}>{h || `Колонка ${i + 1}`}</span>
                <Icon name="chevronRight" size={14} />
                <Menu>
                  <MenuTrigger asChild><Button size="sm" variant={mapping[i] ? 'secondary' : 'ghost'} caret>{mapping[i] ? meta.field(typeId, mapping[i]!)?.name : 'не загружать'}</Button></MenuTrigger>
                  <MenuContent>
                    <MenuItem onSelect={() => setMapping(mapping.map((m, j) => (j === i ? null : m)))}>не загружать</MenuItem>
                    {loadable.map((f) => <MenuItem key={f.id} icon={FIELD_ICONS[f.fieldType]} onSelect={() => setMapping(mapping.map((m, j) => (j === i ? f.key : m)))}>{f.name}</MenuItem>)}
                  </MenuContent>
                </Menu>
                <span className={s.sample}>{body[0]?.[i] ?? ''}</span>
              </div>
            ))}
          </div>
          <div className={s.summary}>
            Готово к загрузке: <b>{prepared!.rows.length.toLocaleString('ru-RU')}</b> из {body.length.toLocaleString('ru-RU')}
            {prepared!.newTerms.length > 0 && <> · будут созданы значения: {prepared!.newTerms.slice(0, 10).map((t) => t.name).join(', ')}{prepared!.newTerms.length > 10 ? ` и ещё ${prepared!.newTerms.length - 10}` : ''}</>}
            {prepared!.newRecords.length > 0 && <> · будут созданы записи: {prepared!.newRecords.slice(0, 10).map((r) => r.title).join(', ')}{prepared!.newRecords.length > 10 ? ` и ещё ${prepared!.newRecords.length - 10}` : ''}</>}
          </div>
          {prepared!.errors.length > 0 && (
            <div className={s.errors}>
              <b>Строки с ошибками не загрузятся ({prepared!.errors.length}):</b>
              {prepared!.errors.map((e) => <div key={e}>{e}</div>)}
            </div>
          )}
          {checked && (
            <div className={s.summary}>
              Проверка: загрузится <b>{checked.imported.toLocaleString('ru-RU')}</b>
              {checked.skipped.length > 0 && <> · пропустится строк: {checked.skipped.length}</>}
              <div className={s.sub}>Ничего не записано — это только проверка файла.</div>
            </div>
          )}
          {checked && checked.skipped.length > 0 && (
            <div className={s.errors}>
              <b>Пропущенные строки ({checked.skipped.length}):</b>
              {checked.skipped.map((miss) => <div key={miss.line}>Строка {miss.line}: {miss.reason}</div>)}
            </div>
          )}
          {result && (
            <div className={s.summary}>
              Загружено: <b>{result.imported.toLocaleString('ru-RU')}</b>
              {result.skipped.length > 0 && <> · пропущено строк: {result.skipped.length}</>}
            </div>
          )}
          {result && result.skipped.length > 0 && (
            <div className={s.errors}>
              <b>Пропущенные строки ({result.skipped.length}):</b>
              {result.skipped.map((miss) => <div key={miss.line}>Строка {miss.line}: {miss.reason}</div>)}
            </div>
          )}
          {runs && (
            <div className={s.errors}>
              <b>Прошлые загрузки ({runs.length}):</b>
              {runs.length === 0 && <div>Загрузок ещё не было.</div>}
              {runs.map((r) => (
                <div key={r.id}>
                  {new Date(r.createdAt).toLocaleString('ru-RU')} · {r.fileName ?? 'без файла'} · {r.status === 'trial' ? `проверка: подойдёт ${r.total - r.skipped}, с замечаниями ${r.skipped}` : r.status === 'refused' ? 'отклонено, ничего не загружено' : `загружено ${r.imported}, пропущено ${r.skipped}`}
                  {r.reasons.length > 0 && <div className={s.sub}>{r.reasons.slice(0, 5).map((x) => `Строка ${x.row}: ${x.reason}`).join('; ')}{r.reasons.length > 5 ? `; и ещё ${r.reasons.length - 5}` : ''}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {error && <div className={s.errors}>{error}</div>}
      {reason !== null && <label className={s.sub}>Причина изменений<Input value={reason} placeholder="Например: перенос из старой CRM" onChange={(e) => setReason(e.target.value)} /></label>}
      <footer className={s.foot}>
        {progress && <span className={s.sub}>Загружено {progress.done.toLocaleString('ru-RU')} из {progress.total.toLocaleString('ru-RU')}</span>}
        <button type="button" className={s.link} onClick={() => void showRuns()}>{runs ? 'Скрыть прошлые загрузки' : 'Прошлые загрузки'}</button>
        <span className={s.grow} />
        <Button onClick={onClose}>{result ? 'Закрыть' : 'Отмена'}</Button>
        {!result && <Button variant="secondary" disabled={!prepared || !prepared.rows.length || !!progress} onClick={() => void run(true)}>Проверить</Button>}
        {!result && <Button variant="primary" disabled={!prepared || !prepared.rows.length || !!progress} onClick={() => void run(false)}>Загрузить</Button>}
      </footer>
    </div>
  );
}
